/**
 * Adapted Multimodal Live Client (event-emitting wrapper around WebSocket)
 */

import { Content, Part } from "@google/generative-ai";
import { EventEmitter } from "eventemitter3";
import { difference } from "lodash";
import {
  ClientContentMessage,
  InputTranscription,
  isInputTranscription,
  isInterrupted,
  isModelTurn,
  isOutputTranscription,
  isServerContenteMessage,
  isSetupCompleteMessage,
  isToolCallCancellationMessage,
  isToolCallMessage,
  isTurnComplete,
  LiveIncomingMessage,
  ModelTurn,
  OutputTranscription,
  RealtimeInputMessage,
  ServerContent,
  StreamingLog,
  ToolCall,
  ToolCallCancellation,
  ToolResponseMessage,
  type LiveConfig,
} from "./multimodal-live-types";
import { blobToJSON, base64ToArrayBuffer } from "./multimodal-live-utils";
import { config } from "./config";

interface MultimodalLiveClientEventTypes {
  open: () => void;
  log: (log: StreamingLog) => void;
  close: (event: CloseEvent) => void;
  audio: (data: ArrayBuffer) => void;
  content: (data: ServerContent) => void;
  interrupted: () => void;
  setupcomplete: () => void;
  status: (status: string) => void;
  turncomplete: () => void;
  toolcall: (toolCall: ToolCall) => void;
  toolcallcancellation: (toolcallCancellation: ToolCallCancellation) => void;
  inputTranscription: (transcription: InputTranscription) => void;
  outputTranscription: (transcription: OutputTranscription) => void;
}

export type MultimodalLiveAPIClientConnection = {
  url?: string;
  runId?: string;
  userId?: string;
};

export class MultimodalLiveClient extends EventEmitter<MultimodalLiveClientEventTypes> {
  public ws: WebSocket | null = null;
  protected config: LiveConfig | null = null;
  public url: string = "";
  private runId: string;
  private userId?: string;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private shouldReconnect = false;
  constructor({ url, userId, runId }: MultimodalLiveAPIClientConnection) {
    super();
    const defaultWsUrl = config.LIVE_WEBSOCKET_URL || `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`;
    url = url || defaultWsUrl;
    this.url = new URL("ws", url).href;
    this.userId = userId;
    this.runId = runId || crypto.randomUUID();
    this.send = this.send.bind(this);
  }

  get currentRunId(): string {
    return this.runId;
  }

  log(type: string, message: StreamingLog["message"]) {
    const log: StreamingLog = { date: new Date(), type, message };
    this.emit("log", log);
  }

  connect(newRunId?: string): Promise<boolean> {
    const ws = new WebSocket(this.url);
    this.shouldReconnect = true;
    if (newRunId) this.runId = newRunId;

    ws.addEventListener("message", async (evt: MessageEvent) => {
      if (evt.data instanceof Blob) {
        this.receive(evt.data);
      } else if (typeof evt.data === "string") {
        try {
          const jsonData = JSON.parse(evt.data);
          if (jsonData.status) {
            this.log("server.status", jsonData.status);
            this.emit("status", jsonData.status);
          }
        } catch {
          // ignore parse errors for non-JSON messages
        }
      }
    });

    return new Promise((resolve, reject) => {
      const onError = (ev: Event) => {
        this.disconnect(ws);
        const message = `Could not connect to "${this.url}"`;
        this.log(`server.${ev.type}`, message);
        reject(new Error(message));
      };
      ws.addEventListener("error", onError);
      ws.addEventListener("open", (ev: Event) => {
        this.log(`client.${ev.type}`, `connected to socket`);
        this.emit("open");
        this.ws = ws;
        this.reconnectAttempts = 0;
        const setupMessage = { setup: { run_id: this.runId, user_id: this.userId } } as any;
        this._sendDirect(setupMessage);
        ws.removeEventListener("error", onError);
        ws.addEventListener("close", (ev: CloseEvent) => {
          this.disconnect(ws);
          let reason = ev.reason || "";
          if (reason.toLowerCase().includes("error")) {
            const prelude = "ERROR]";
            const idx = reason.indexOf(prelude);
            if (idx > 0) {
              reason = reason.slice(idx + prelude.length + 1);
            }
          }
          this.log(`server.${ev.type}`, `disconnected ${reason ? `with reason: ${reason}` : ``}`);
          this.emit("close", ev);
          const nonRecoverable = [1000, 1007, 1009, 1011, 1012, 1013, 1014, 1015];
          if (this.shouldReconnect && !nonRecoverable.includes(ev.code) && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect();
          }
        });
        resolve(true);
      });
    });
  }

  authenticate(token: string) {
    if (!token) return;
    this._sendDirect({ type: 'authenticate', token });
  }

  sendMetadata({ sessionId, topicId, documentId, mode }: { sessionId?: string | null; topicId?: string | null; documentId?: string | null; mode?: string | null; }) {
    this._sendDirect({ type: 'metadata', session_id: sessionId, topic_id: topicId, document_id: documentId, mode });
  }

  disconnect(ws?: WebSocket) {
    if ((!ws || this.ws === ws) && this.ws) {
      this.shouldReconnect = false;
      if (this.reconnectTimeout) { clearTimeout(this.reconnectTimeout); this.reconnectTimeout = null; }
      this.ws.close();
      this.ws = null;
      this.log("client.close", `Disconnected`);
      return true;
    }
    return false;
  }

  protected async receive(blob: Blob) {
    const response = (await blobToJSON(blob)) as LiveIncomingMessage;

    if (isToolCallMessage(response)) {
      this.log("server.toolCall", JSON.stringify(response.toolCall));
      this.emit("toolcall", response.toolCall);
      return;
    }
    if (isToolCallCancellationMessage(response)) {
      this.log("receive.toolCallCancellation", response);
      this.emit("toolcallcancellation", response.toolCallCancellation);
      return;
    }

    if (isSetupCompleteMessage(response)) {
      this.log("server.send", "setupComplete");
      this.emit("setupcomplete");
      return;
    }

    if (isServerContenteMessage(response)) {
      const { serverContent } = response;
      if (isInputTranscription(serverContent)) {
        this.log("server.inputTranscription", serverContent.inputTranscription.text);
        this.emit("inputTranscription", serverContent);
      }
      if (isOutputTranscription(serverContent)) {
        this.log("server.outputTranscription", serverContent.outputTranscription.text);
        this.emit("outputTranscription", serverContent);
      }
      if (isInterrupted(serverContent)) {
        this.log("receive.serverContent", "interrupted");
        this.emit("interrupted");
        return;
      }
      if (isTurnComplete(serverContent)) {
        this.log("server.send", "turnComplete");
        this.emit("turncomplete");
      }
      if (isModelTurn(serverContent)) {
        let parts: Part[] = serverContent.modelTurn.parts;
        const audioParts = parts.filter((p) => p.inlineData && p.inlineData.mimeType.startsWith("audio/pcm"));
        const base64s = audioParts.map((p) => p.inlineData?.data);
        const otherParts = difference(parts, audioParts);
        base64s.forEach((b64) => {
          if (b64) {
            const data = base64ToArrayBuffer(b64);
            this.emit("audio", data);
            this.log(`server.audio`, `buffer (${data.byteLength})`);
          }
        });
        if (!otherParts.length) return;
        parts = otherParts;
        const content: ModelTurn = { modelTurn: { parts } };
        this.emit("content", content);
        this.log(`server.content`, response);
      }
    } else {
      this.log("received unmatched message", response as any);
    }
  }

  sendRealtimeInput(chunks: any[]) {
    const data: RealtimeInputMessage = { realtimeInput: { mediaChunks: chunks } };
    this._sendDirect(data);
    this.log(`client.realtimeInput`, `chunks=${chunks.length}`);
  }

  sendToolResponse(toolResponse: ToolResponseMessage["toolResponse"]) {
    const message: ToolResponseMessage = { toolResponse };
    this._sendDirect(message);
    this.log(`client.toolResponse`, message);
  }

  send(parts: Part | Part[], turnComplete: boolean = true) {
    parts = Array.isArray(parts) ? parts : [parts];
    const content: Content = { role: "user", parts };
    const clientContentRequest: ClientContentMessage = { clientContent: { turns: [content], turnComplete } };
    this._sendDirect(clientContentRequest);
    this.log(`client.send`, clientContentRequest);
  }

  sendContextUpdate(contextUpdate: object) {
    const message = { type: "context_update", payload: contextUpdate };
    this._sendDirect(message);
    this.log("client.contextUpdate", JSON.stringify(message));
  }

  _sendDirect(request: object) {
    if (!this.ws) throw new Error("WebSocket is not connected");
    const str = JSON.stringify(request);
    this.ws.send(str);
  }

  private scheduleReconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 10000);
    this.reconnectAttempts++;
    this.log('client.reconnect', `attempt=${this.reconnectAttempts} delay=${delay}`);
    this.reconnectTimeout = setTimeout(() => {
      if (!this.ws && this.shouldReconnect && this.reconnectAttempts <= this.maxReconnectAttempts) {
        this.connect().catch(() => {});
      }
    }, delay);
  }
}
